<template>
  <div class="flex flex-col warehouse-audit" :class="{ 'is-compact': compact }">
    <div class="history-toolbar">
      <input v-model="search_val" type="text" class="border-slate-200 rounded border px-1 text-14px w-48 my-2" placeholder="Kerko" />
    </div>
    <div v-if="loading" class="py-4 text-14px has-text-grey">Duke ngarkuar historikun...</div>
    <div v-else-if="!filteredItems.length" class="py-4 text-14px has-text-grey">Nuk ka veprime të audituara për këtë magazinë.</div>
    <div v-else class="overflow-x-auto">
      <table class="table is-narrow is-fullwidth">
        <thead>
          <tr>
            <th></th>
            <th>Kush</th>
            <th>Veprimi</th>
            <th>Ku</th>
            <th>Kur</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in pagedItems" :key="row.id">
            <tr class="text-14px" :class="{ 'is-expanded': expandedId === row.id }">
              <td style="width: 36px">
                <button
                  v-if="hasDetails(row)"
                  type="button"
                  class="button is-small is-light"
                  @click="expandedId = expandedId === row.id ? null : row.id"
                >
                  {{ expandedId === row.id ? "−" : "+" }}
                </button>
              </td>
              <td>{{ row.actor_name || "—" }}</td>
              <td>
                <div>{{ row.action_label || row.action }}</div>
                <div v-if="row.source_label" class="has-text-grey history-desc">{{ row.source_label }}</div>
              </td>
              <td class="audit-ku-cell">
                <div v-if="changeEntries(row).length">
                  <div v-for="(entry, idx) in changeEntries(row)" :key="idx" class="audit-change-line">
                    <span class="has-text-weight-semibold">{{ entry.label }}</span>
                    : {{ entry.old }} → {{ entry.new }}
                  </div>
                </div>
                <div v-else class="has-text-grey">{{ row.source_label || row.description || "—" }}</div>
              </td>
              <td>{{ formatDateTime(row.created_at) }}</td>
            </tr>
            <tr v-if="expandedId === row.id && hasDetails(row)" class="audit-detail-row">
              <td colspan="5">
                <div class="audit-detail-card">
                  <div v-if="changeEntries(row).length" class="mb-2">
                    <div class="has-text-weight-semibold mb-1" style="font-size: 12px">Ndryshimet</div>
                    <table class="table is-narrow is-fullwidth" style="font-size: 12px">
                      <thead>
                        <tr>
                          <th>Fusha</th>
                          <th>Para</th>
                          <th>Mbas</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(entry, idx) in changeEntries(row)" :key="idx">
                          <td>{{ entry.label }}</td>
                          <td>{{ entry.old }}</td>
                          <td>{{ entry.new }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else-if="row.description" class="has-text-grey" style="font-size: 12px">{{ row.description }}</div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div class="flex justify-center items-center mt-4 space-x-2 text-14px">
        <button @click="prevPage" :disabled="current_page === 1" v-show="current_page > 1" class="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50">
          <i class="fa-solid fa-angle-left"></i>
        </button>
        <span>{{ current_page }}</span>
        <button
          @click.prevent="nextPage"
          :disabled="current_page === totalPages"
          v-show="current_page < totalPages"
          class="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
        >
          <i class="fa-solid fa-angle-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import WarehousesServices from "@/services/Warehouse";

export default {
  name: "WarehouseAudit",
  props: {
    warehouseGuid: { type: String, default: "" },
    reloadKey: { default: 0 },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      items: [],
      search_val: "",
      current_page: 1,
      items_per_page: 8,
      expandedId: null,
    };
  },
  watch: {
    warehouseGuid: {
      immediate: true,
      handler() {
        this.load();
      },
    },
    reloadKey() {
      this.load();
    },
    search_val() {
      this.current_page = 1;
    },
  },
  computed: {
    filteredItems() {
      const q = (this.search_val || "").toLowerCase().trim();
      if (!q) return this.items;
      return this.items.filter((row) => JSON.stringify(row).toLowerCase().includes(q));
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.items_per_page));
    },
    pagedItems() {
      const start = (this.current_page - 1) * this.items_per_page;
      return this.filteredItems.slice(start, start + this.items_per_page);
    },
  },
  methods: {
    async load() {
      if (!this.warehouseGuid) {
        this.items = [];
        return;
      }
      this.loading = true;
      this.current_page = 1;
      this.expandedId = null;
      try {
        const data = await WarehousesServices.getWarehouseAudit(this.warehouseGuid);
        this.items = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    changeEntries(row) {
      if (Array.isArray(row?.changes_list) && row.changes_list.length) {
        return row.changes_list;
      }
      const changes = row?.changes;
      if (!changes || typeof changes !== "object") return [];
      return Object.keys(changes).map((field) => {
        const change = changes[field] || {};
        const label = change.label || field;
        return {
          field,
          label,
          old: this.formatValue(change.old),
          new: this.formatValue(change.new),
          text: `${label}: ${this.formatValue(change.old)} → ${this.formatValue(change.new)}`,
        };
      });
    },
    hasDetails(row) {
      return this.changeEntries(row).length > 0 || !!row.description;
    },
    formatValue(value) {
      if (value === null || value === undefined || value === "") return "—";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    formatDateTime(value) {
      if (!value) return "—";
      const d = new Date(String(value).replace(" ", "T"));
      if (Number.isNaN(d.getTime())) return value;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    prevPage() {
      if (this.current_page > 1) this.current_page -= 1;
    },
    nextPage() {
      if (this.current_page < this.totalPages) this.current_page += 1;
    },
  },
};
</script>

<style scoped>
.history-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.history-desc {
  font-size: 12px;
}
.audit-ku-cell {
  min-width: 140px;
}
.audit-change-line {
  font-size: 12px;
  line-height: 1.35;
  word-break: break-word;
}
.warehouse-audit.is-compact .table {
  font-size: 12px;
}
.warehouse-audit.is-compact th,
.warehouse-audit.is-compact td {
  white-space: normal;
  word-break: break-word;
}
.warehouse-audit.is-compact .history-desc {
  display: none;
}
.audit-detail-card {
  background: #f8fafc;
  padding: 8px;
  border-radius: 4px;
}
</style>
